import { useMemo, useState } from 'react';
import { useTable } from 'spacetimedb/react';
import { tables } from '@vidmakr/bindings';
import type { Asset, AssetReview, ReviewDecision, ReviewLink } from '@vidmakr/bindings/types';
import { Check, RotateCcw, Square, X } from 'lucide-react';
import { CommentBody } from '@/components/mentions';
import { timeAgo } from '@/lib/format';
import { reducers } from '@/lib/stdb';
import { attempt } from '@/lib/toast';
import { REVIEW_LABEL } from './Inspectors';
import { Badge, Button, Textarea, cx } from './ui';

type Decision = 'approved' | 'changes_requested';

/** The status badge of an asset's review (nothing while it is not in review). */
export function AssetReviewBadge({ review }: { review: AssetReview | undefined }) {
  const label = review ? REVIEW_LABEL[review.status] : undefined;
  if (!label) return null;
  return (
    <span className="inline-flex shrink-0" data-asset-review-badge={review!.status}>
      <Badge tone={label.tone}>{label.label}</Badge>
    </span>
  );
}

/**
 * An asset's review in its share dialog: its status, who approved or asked
 * for changes in the current round (guests through links, members here), and
 * the team's own decision. Members can start a new round or end the review.
 */
export function AssetReviewPanel({ asset, links }: { asset: Asset; links: readonly ReviewLink[] }) {
  const [rows] = useTable(tables.assetReview.where((r) => r.assetId.eq(asset.id)));
  const [decisionRows] = useTable(tables.reviewDecision.where((d) => d.assetId.eq(asset.id)));
  const review = rows[0] as AssetReview | undefined;
  const [deciding, setDeciding] = useState<Decision | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const linkLabel = useMemo(() => new Map(links.map((l) => [l.id, l.label])), [links]);
  const open = !!review && review.status !== 'editing';
  // This round's decisions, newest first.
  const current = useMemo(
    () =>
      (open ? decisionRows.filter((d) => d.round === review.round) : []).sort((a, b) => Number(b.createdAt.microsSinceUnixEpoch - a.createdAt.microsSinceUnixEpoch)),
    [decisionRows, open, review?.round], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const submit = async () => {
    if (!deciding) return;
    setBusy(true);
    const ok = await attempt(() => reducers().submitAssetReview({ assetId: asset.id, decision: deciding, note }), deciding === 'approved' ? 'Approved' : 'Changes requested');
    setBusy(false);
    if (ok) {
      setDeciding(null);
      setNote('');
    }
  };
  const round = (openRound: boolean) => void attempt(() => reducers().setAssetReviewRound({ assetId: asset.id, open: openRound }), openRound ? 'New review round started' : 'Review ended');
  const label = review ? REVIEW_LABEL[review.status] : undefined;

  return (
    <div className="mb-4 overflow-hidden rounded-xl border border-line" data-asset-review={review?.status ?? 'editing'}>
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2">
        <span className="text-xs font-semibold text-muted">Review</span>
        {label ? <Badge tone={label.tone}>{label.label}</Badge> : <span className="text-xs text-faint">Not in review</span>}
        {open ? (
          <span className="text-[11px] text-faint" data-asset-review-round={review.round}>
            Round {review.round}
            {review.requestedByName ? ` · started by ${review.requestedByName}` : ''}
          </span>
        ) : null}
        <div className="flex-1" />
        {review && review.status !== 'in_review' ? (
          <Button size="sm" variant="ghost" onClick={() => round(true)} title="Ask for approval again; earlier decisions stay as history" data-asset-review-new-round>
            <RotateCcw size={12} /> New round
          </Button>
        ) : null}
        {open ? (
          <Button size="sm" variant="ghost" onClick={() => round(false)} title="Take the file out of review" data-asset-review-end>
            <Square size={11} /> End review
          </Button>
        ) : null}
      </div>
      {current.length ? (
        <div className="max-h-48 divide-y divide-line overflow-y-auto">
          {current.map((d) => (
            <DecisionRow key={d.id} d={d} via={d.linkId ? (linkLabel.get(d.linkId) ?? 'a deleted link') : ''} />
          ))}
        </div>
      ) : (
        <div className="px-3 py-3 text-xs text-faint">
          {open ? 'No decisions yet in this round.' : 'Turn on approvals for a link, or decide here, to start a review.'}
        </div>
      )}
      <div className="border-t border-line bg-panel-2 px-3 py-2.5">
        {deciding ? (
          <div className="space-y-2">
            <Textarea
              rows={2}
              autoFocus
              placeholder={deciding === 'approved' ? 'Anything to add? (optional)' : 'What needs to change? (optional)'}
              value={note}
              maxLength={2000}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              data-asset-decision-note
            />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setDeciding(null)}>
                Cancel
              </Button>
              <Button size="sm" variant="primary" className={deciding === 'approved' ? 'bg-ok hover:bg-ok/90' : ''} loading={busy} onClick={() => void submit()} data-asset-decision-submit>
                {deciding === 'approved' ? <Check size={13} /> : <X size={13} />} {deciding === 'approved' ? 'Approve' : 'Request changes'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="secondary" onClick={() => setDeciding('changes_requested')} data-asset-request-changes>
              <X size={13} /> Request changes
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setDeciding('approved')} data-asset-approve>
              <Check size={13} /> Approve
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function DecisionRow({ d, via }: { d: ReviewDecision; via: string }) {
  const approved = d.decision === 'approved';
  return (
    <div className="px-3 py-2 text-sm" data-asset-decision-row={d.decision}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className={cx('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px]', approved ? 'bg-ok/15 text-ok' : 'bg-warn/15 text-warn')}>
          {approved ? <Check size={11} /> : <X size={11} />}
          {approved ? 'Approved' : 'Changes requested'}
        </span>
        <span className="font-medium">{d.name}</span>
        <span className="text-[11px] text-faint">
          {via ? `via “${via}” · ` : ''}
          {timeAgo(d.createdAt)}
        </span>
      </div>
      {d.note ? <CommentBody body={d.note} className="mt-0.5 block whitespace-pre-wrap break-words text-xs text-muted" /> : null}
    </div>
  );
}
